import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  MessageCircle, X, Send, Loader2, Bot, User as UserIcon,
  LifeBuoy, Mail, ChevronDown, TicketCheck,
} from "lucide-react";
import { useAuth } from "@/App";

const SESSION_STORE_KEY = "tbc_support_session_id";

function getOrCreateSessionId() {
  try {
    let s = localStorage.getItem(SESSION_STORE_KEY);
    if (!s) {
      s = `sup_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
      localStorage.setItem(SESSION_STORE_KEY, s);
    }
    return s;
  } catch {
    return `sup_${Math.random().toString(36).slice(2, 12)}`;
  }
}

const WELCOME = `Hi! I'm the Blk Community support assistant. I can help with:

• Subscription plans & upgrades
• Claiming a business
• Stripe payouts
• Finding a specific business

If I can't resolve it, I'll help you file a ticket for our team. What's up?`;

/**
 * SupportChatBubble — floating chat launcher pinned to bottom-right.
 * Opens a chat panel powered by Claude Sonnet (streaming SSE).
 * Signed-in users only — anonymous visitors see a "Sign in to chat" nudge.
 */
export default function SupportChatBubble() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => getOrCreateSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketBody, setTicketBody] = useState("");
  const [filingTicket, setFilingTicket] = useState(false);
  const scrollRef = useRef(null);
  const streamAbortRef = useRef(null);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  useEffect(() => { scrollToBottom(); }, [messages, sending]);

  // Load prior history when we open for the first time
  const loadHistory = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/support/history`, {
        params: { session_id: sessionId },
        withCredentials: true,
      });
      const prior = r.data.messages || [];
      if (prior.length > 0) {
        setMessages(prior.map((m) => ({ role: m.role, content: m.content })));
      } else {
        setMessages([{ role: "assistant", content: WELCOME }]);
      }
    } catch {
      setMessages([{ role: "assistant", content: WELCOME }]);
    }
  }, [user, sessionId]);

  useEffect(() => {
    if (open && messages.length === 0) loadHistory();
  }, [open, loadHistory, messages.length]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending || !user) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/support/chat`,
        { session_id: sessionId, message: text },
        { withCredentials: true, timeout: 60000 }
      );
      const reply = r.data?.reply || "Hmm, I couldn't come up with a response. Try again or file a ticket.";
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant") {
          next[next.length - 1] = { role: "assistant", content: reply };
        }
        return next;
      });
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant" && !last.content) {
          next[next.length - 1] = {
            role: "assistant",
            content:
              e.response?.data?.detail ||
              "Sorry, I couldn't reach the assistant right now. Please try again, or file a ticket below.",
          };
        }
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  const fileTicket = async () => {
    if (!ticketSubject.trim() || !ticketBody.trim()) {
      toast.error("Please fill both fields.");
      return;
    }
    setFilingTicket(true);
    try {
      await axios.post(
        `${API}/support/ticket`,
        {
          session_id: sessionId,
          subject: ticketSubject.trim(),
          description: ticketBody.trim(),
        },
        { withCredentials: true }
      );
      toast.success("Ticket filed — we'll email you within 24 hours.");
      setTicketOpen(false);
      setTicketSubject("");
      setTicketBody("");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Got it — ticket filed. Our team will reach out to you at " +
            (user?.email || "your email") +
            " within 24 hours. You can also email hello@theblkcommunity.com anytime.",
        },
      ]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't file ticket");
    } finally {
      setFilingTicket(false);
    }
  };

  return (
    <>
      {/* Launcher bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-40 h-14 w-14 rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-tbc-gold shadow-2xl flex items-center justify-center transition-transform hover:scale-105"
          aria-label="Open support chat"
          data-testid="support-chat-open"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-4 right-4 z-50 w-[min(400px,calc(100vw-2rem))] max-h-[calc(100vh-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          data-testid="support-chat-panel"
        >
          {/* Header */}
          <div className="bg-tbc-navy text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-tbc-gold/20 flex items-center justify-center">
                <LifeBuoy className="w-4 h-4 text-tbc-gold" />
              </div>
              <div>
                <p className="font-heading text-sm">TBC Support</p>
                <p className="text-xs text-white/60">Powered by Claude</p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white transition-colors p-1"
              aria-label="Close chat"
              data-testid="support-chat-close"
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          {!user ? (
            <div className="p-6 text-center space-y-3 flex-1 flex flex-col justify-center">
              <UserIcon className="w-10 h-10 text-gray-300 mx-auto" />
              <p className="text-sm text-gray-600 font-body">
                Please sign in to chat with support.
              </p>
              <a
                href="/login"
                className="inline-block bg-tbc-navy text-white text-sm px-4 py-2 rounded-full hover:bg-tbc-navy/90"
                data-testid="support-chat-signin"
              >
                Sign in
              </a>
              <p className="text-xs text-gray-400">
                Or email us at{" "}
                <a href="mailto:hello@theblkcommunity.com" className="underline">
                  hello@theblkcommunity.com
                </a>
              </p>
            </div>
          ) : ticketOpen ? (
            <div className="p-4 flex-1 flex flex-col gap-3 overflow-y-auto" data-testid="support-ticket-form">
              <p className="text-sm text-gray-600 font-body">
                Tell us what&apos;s going on and we&apos;ll get back to you at{" "}
                <strong>{user.email}</strong>.
              </p>
              <Input
                placeholder="Subject (e.g. Upgrade failing on my business)"
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                maxLength={200}
                data-testid="support-ticket-subject"
              />
              <Textarea
                placeholder="Describe the issue in as much detail as you can…"
                value={ticketBody}
                onChange={(e) => setTicketBody(e.target.value)}
                rows={6}
                maxLength={4000}
                data-testid="support-ticket-body"
              />
              <p className="text-xs text-gray-400 font-body">
                Your recent chat transcript will be attached automatically.
              </p>
              <div className="flex gap-2 mt-auto">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setTicketOpen(false)}
                  disabled={filingTicket}
                >
                  Back to chat
                </Button>
                <Button
                  className="flex-1 bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                  onClick={fileTicket}
                  disabled={filingTicket}
                  data-testid="support-ticket-submit"
                >
                  {filingTicket ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TicketCheck className="w-4 h-4 mr-2" />
                  )}
                  File ticket
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/60"
                data-testid="support-chat-messages"
              >
                {messages.map((m, i) => (
                  <div
                    key={`msg-${i}-${m.role}`}
                    className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {m.role === "assistant" && (
                      <div className="w-7 h-7 rounded-full bg-tbc-navy flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Bot className="w-3.5 h-3.5 text-tbc-gold" />
                      </div>
                    )}
                    <div
                      className={`px-3 py-2 rounded-2xl text-sm font-body max-w-[80%] whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-tbc-gold text-tbc-navy rounded-br-sm"
                          : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm"
                      }`}
                    >
                      {m.content || (sending && i === messages.length - 1 ? "…" : "")}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer with ticket + input */}
              <div className="border-t border-gray-100 bg-white p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => setTicketOpen(true)}
                    className="text-xs text-tbc-navy hover:text-tbc-gold flex items-center gap-1"
                    data-testid="support-file-ticket-btn"
                  >
                    <TicketCheck className="w-3 h-3" />
                    File a ticket instead
                  </button>
                  <a
                    href="mailto:hello@theblkcommunity.com"
                    className="text-xs text-gray-400 hover:text-tbc-navy flex items-center gap-1"
                  >
                    <Mail className="w-3 h-3" />
                    hello@theblkcommunity.com
                  </a>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Type your message…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={sending}
                    maxLength={2000}
                    data-testid="support-chat-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={sending || !input.trim()}
                    className="bg-tbc-navy hover:bg-tbc-navy/90 text-white flex-shrink-0"
                    data-testid="support-chat-send"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
